import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { CircleNotch, CheckCircle, ArrowRight } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/context/AuthContext";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setError("Passwords don't match."); return; }
    setBusy(true);
    setError(null);
    try {
      await api.resetPassword(token, password);
      setDone(true);
      setTimeout(() => nav("/login"), 2200);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen place-items-center px-6">
      <div className="w-full max-w-sm">
        {!token ? (
          <div data-testid="reset-no-token" className="text-center">
            <h2 className="font-serif text-2xl">Invalid reset link</h2>
            <p className="mt-2 text-sm text-muted-foreground">This link is missing its token. Please request a new reset from the sign-in page.</p>
            <Link to="/login" className="mt-4 inline-block text-sm font-medium text-foreground underline underline-offset-2">Back to sign in</Link>
          </div>
        ) : done ? (
          <div data-testid="reset-done" className="text-center">
            <CheckCircle size={30} weight="fill" style={{ color: "hsl(var(--tag-preference))" }} className="mx-auto" />
            <h2 className="mt-3 font-serif text-2xl">Password updated</h2>
            <p className="mt-2 text-sm text-muted-foreground">Redirecting you to sign in…</p>
          </div>
        ) : (
          <>
            <h2 data-testid="reset-heading" className="font-serif text-3xl">Set a new password</h2>
            <p className="mt-2 text-sm text-muted-foreground">Choose a new password for your account.</p>
            <form onSubmit={submit} className="mt-7 space-y-4">
              <div>
                <label className="mb-1 block text-xs font-medium text-muted-foreground">New password</label>
                <input data-testid="reset-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="w-full rounded-sm border border-border bg-background px-3 py-2.5 text-sm outline-none focus:border-foreground" placeholder="••••••••" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-muted-foreground">Confirm password</label>
                <input data-testid="reset-confirm-input" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className="w-full rounded-sm border border-border bg-background px-3 py-2.5 text-sm outline-none focus:border-foreground" placeholder="••••••••" />
              </div>
              {error && <p data-testid="reset-error" className="text-sm" style={{ color: "hsl(var(--destructive))" }}>{error}</p>}
              <button data-testid="reset-submit-btn" type="submit" disabled={busy} className="inline-flex w-full items-center justify-center gap-2 rounded-sm border border-primary bg-primary px-5 py-3 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/85 disabled:opacity-50">
                {busy ? <CircleNotch size={16} className="animate-spin" /> : <ArrowRight size={16} weight="bold" />} Update password
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
